import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import {
  BookOpen, Headphones, Video, Sparkles, Waves, ScrollText,
  GraduationCap, FlaskConical, Library as LibraryIcon, ArrowLeft,
} from "lucide-react";
import { ACTIVATIONS, BOOKS, RELEASES, WISDOM, type WisdomPost, type Activation, type Release } from "@/lib/content";
import { absoluteUrl } from "@/lib/site";

type Category =
  | "Books" | "Music" | "Videos" | "Meditations" | "Affirmations"
  | "Articles" | "Teachings" | "Research" | "Collections";

interface CategoryMeta {
  slug: string;
  name: Category;
  headline: string;
  description: string;
  icon: typeof BookOpen;
}

const CATEGORIES: CategoryMeta[] = [
  { slug: "books",         name: "Books",         headline: "Books & Long-form Study",  description: "Full-length books from the KAfrequency canon.",                icon: BookOpen },
  { slug: "music",         name: "Music",         headline: "Music & Frequency Releases", description: "Every sonic transmission — albums, singles, and sutras.",     icon: Headphones },
  { slug: "videos",        name: "Videos",        headline: "Video Transmissions",       description: "Visual teachings and cinematic transmissions.",                icon: Video },
  { slug: "meditations",   name: "Meditations",   headline: "Meditations & Activations", description: "Guided practices, breathwork, and daily activations.",         icon: Waves },
  { slug: "affirmations",  name: "Affirmations",  headline: "Affirmations & Mantras",    description: "Sacred phrases and mantric activations to repeat and embody.", icon: Sparkles },
  { slug: "articles",      name: "Articles",      headline: "Articles & Essays",         description: "Written reflections, mirror thoughts, and artist notes.",      icon: ScrollText },
  { slug: "teachings",     name: "Teachings",     headline: "Teachings",                 description: "Core teachings on discipline, presence, and inner mechanics.", icon: GraduationCap },
  { slug: "research",      name: "Research",      headline: "Research & Inner Technology", description: "Sacred systems, spiritual mechanics, and structured study.", icon: FlaskConical },
  { slug: "collections",   name: "Collections",   headline: "Collections",               description: "Permanent portals into grouped bodies of work.",               icon: LibraryIcon },
];

const BY_SLUG = new Map(CATEGORIES.map((c) => [c.slug, c]));

const RESEARCH_CATS = new Set(["Sacred Systems", "Spiritual Mechanics", "Inner Technology"]);
const ARTICLE_CATS = new Set(["Mirror Thoughts", "Essays", "Artist Notes", "Behind the Music"]);

function classifyWisdom(w: WisdomPost): Category {
  if (RESEARCH_CATS.has(w.category)) return "Research";
  if (ARTICLE_CATS.has(w.category)) return "Articles";
  return "Teachings";
}
function classifyActivation(a: Activation): Category {
  if (/mantra|phrase|affirm/i.test(a.category)) return "Affirmations";
  return "Meditations";
}
function classifyRelease(r: Release): Category {
  return /video|film|visual/i.test(r.type) ? "Videos" : "Music";
}

interface Entry { title: string; subtitle: string; href: string; meta?: string; }

const COLLECTION_ENTRIES: Entry[] = [
  { title: "The Complete Discography",  subtitle: "Every KAfrequency release, sutra, and frequency transmission.", href: "/music" },
  { title: "Daily Activations Archive", subtitle: "Rising, midday, evening, and night-seal activations.",         href: "/activations" },
  { title: "The Written Canon",         subtitle: "Long-form books and study editions.",                          href: "/books" },
  { title: "The 47 Gates Path",         subtitle: "The consciousness gate sequence and journey map.",             href: "/gates" },
  { title: "Personal Playlists",        subtitle: "Curated cycles you build for your own practice.",              href: "/playlists" },
  { title: "The Codex",                 subtitle: "Concepts, symbols, and inner-technology references.",          href: "/codex" },
];

function entriesFor(name: Category): Entry[] {
  const out: Entry[] = [];
  if (name === "Books") {
    for (const b of BOOKS) out.push({ title: b.title, subtitle: b.subtitle, href: `/books/${b.slug}` });
    return out;
  }
  if (name === "Collections") return COLLECTION_ENTRIES;

  for (const w of WISDOM) {
    if (classifyWisdom(w) === name) out.push({ title: w.title, subtitle: w.excerpt, href: `/wisdom/${w.slug}`, meta: w.readingTime });
  }
  for (const a of ACTIVATIONS) {
    if (classifyActivation(a) === name) out.push({ title: a.title, subtitle: a.intention, href: `/activations/${a.slug}`, meta: a.duration });
  }
  for (const r of RELEASES) {
    if (classifyRelease(r) === name) out.push({ title: r.title, subtitle: r.statement ?? r.type, href: `/music/${r.slug}` });
  }
  return out;
}

export const CATEGORY_SLUGS = CATEGORIES.map((c) => c.slug);

export const Route = createFileRoute("/library/$category")({
  loader: ({ params }) => {
    const meta = BY_SLUG.get(params.category);
    if (!meta) throw notFound();
    return { slug: meta.slug };
  },
  head: ({ loaderData }) => {
    if (!loaderData) {
      return { meta: [{ title: "Category not found — The Library" }, { name: "robots", content: "noindex" }] };
    }
    const meta = BY_SLUG.get(loaderData.slug)!;
    const url = absoluteUrl(`/library/${meta.slug}`);
    const title = `${meta.headline} — The Library · KAfrequency`;
    return {
      meta: [
        { title },
        { name: "description", content: meta.description },
        { property: "og:title", content: title },
        { property: "og:description", content: meta.description },
        { property: "og:type", content: "website" },
        { property: "og:url", content: url },
        { property: "og:site_name", content: "Buddha KA · KAfrequency" },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: title },
        { name: "twitter:description", content: meta.description },
      ],
      links: [{ rel: "canonical", href: url }],
    };
  },
  component: LibraryCategoryPage,
  notFoundComponent: () => (
    <div className="mx-auto max-w-2xl px-4 py-24 text-center">
      <h1 className="font-serif text-3xl">Category not found</h1>
      <Link to="/library" className="mt-6 inline-block text-gold hover:text-gold-luminous">← Return to the Library</Link>
    </div>
  ),
});

function LibraryCategoryPage() {
  const { slug } = Route.useLoaderData();
  const meta = BY_SLUG.get(slug)!;
  const items = entriesFor(meta.name);
  const Icon = meta.icon;

  return (
    <div className="mx-auto max-w-5xl px-4 py-16 sm:px-6 sm:py-20">
      <Link to="/library" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.3em] text-muted-foreground hover:text-gold">
        <ArrowLeft size={12} /> The Library
      </Link>

      <header className="mt-8">
        <div className="flex items-center gap-3">
          <Icon size={16} className="text-gold" />
          <p className="text-[10px] uppercase tracking-[0.4em] text-gold">{meta.name}</p>
        </div>
        <h1 className="mt-3 font-serif text-4xl sm:text-5xl">{meta.headline}</h1>
        <p className="mt-4 max-w-2xl text-muted-foreground">{meta.description}</p>
        <p className="mt-3 text-xs text-muted-foreground">{items.length} {items.length === 1 ? "entry" : "entries"}</p>
      </header>

      {items.length === 0 ? (
        <div className="mt-16 rounded-2xl border hairline bg-obsidian/40 p-16 text-center">
          <p className="font-serif text-2xl text-ivory">This shelf is still being filled.</p>
          <p className="mt-2 text-sm text-muted-foreground">New transmissions arrive here soon.</p>
        </div>
      ) : (
        <ul className="mt-12 divide-y hairline border-y hairline">
          {items.map((it) => (
            <li key={it.href}>
              <a href={it.href} className="group flex items-baseline justify-between gap-6 py-5 hover:bg-charcoal/40">
                <div className="min-w-0">
                  <h2 className="font-serif text-xl group-hover:text-gold-luminous">{it.title}</h2>
                  <p className="mt-1 line-clamp-2 text-sm text-muted-foreground">{it.subtitle}</p>
                  {it.meta && <p className="mt-1 text-[10px] uppercase tracking-widest text-gold/80">{it.meta}</p>}
                </div>
                <span className="shrink-0 text-xs text-gold">Open →</span>
              </a>
            </li>
          ))}
        </ul>
      )}

      <nav aria-label="Other shelves" className="mt-20">
        <p className="text-[10px] uppercase tracking-[0.3em] text-gold">Other shelves</p>
        <ul className="mt-4 flex flex-wrap gap-2">
          {CATEGORIES.filter((c) => c.slug !== meta.slug).map((c) => (
            <li key={c.slug}>
              <Link
                to="/library/$category"
                params={{ category: c.slug }}
                className="inline-flex items-center gap-2 rounded-full border hairline px-4 py-1.5 text-xs uppercase tracking-[0.2em] text-muted-foreground hover:border-gold hover:text-gold"
              >
                {c.name}
              </Link>
            </li>
          ))}
        </ul>
      </nav>
    </div>
  );
}
